import type { LibraryItem } from "@/api/models/api-types";
import { getFileUrl } from "@/lib/utils";

export type LibraryMediaKind = "image" | "video" | "audio" | "document";

const AUDIO_EXTENSIONS = ["mp3", "wav", "m4a", "aac", "flac", "oga", "ogg"];
const VIDEO_EXTENSIONS = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];
const DOCUMENT_EXTENSIONS = ["pdf", "txt", "doc", "docx"];

export const getFileExtension = (item: LibraryItem) =>
  item.file.fileUrl.split(".").pop()?.toLowerCase() ?? "";

// Öğenin türü: önce item.type, sonra MIME, en son dosya uzantısı
export const getMediaKind = (item: LibraryItem): LibraryMediaKind => {
  if (item.type === "pdf") return "document";
  if (["image", "video", "audio", "document"].includes(item.type)) {
    return item.type as LibraryMediaKind;
  }

  const mime = item.file.type ?? "";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf" || mime === "text/plain") return "document";

  const extension = getFileExtension(item);
  if (AUDIO_EXTENSIONS.includes(extension)) return "audio";
  if (VIDEO_EXTENSIONS.includes(extension)) return "video";
  if (DOCUMENT_EXTENSIONS.includes(extension)) return "document";
  return "image";
};

export const getItemUrl = (item: LibraryItem) => getFileUrl(item.file) || "";

export const getItemFileName = (item: LibraryItem) => {
  const fromUrl = item.file.fileUrl.split("/").pop();
  if (fromUrl) return fromUrl;
  return `${item.title || `file-${Date.now()}`}.${getFileExtension(item) || "bin"}`;
};

export const formatFileSize = (bytes?: number) => {
  if (!bytes) return null;
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

/**
 * Dosyayı blob olarak indirir. `download` attribute'ü cross-origin URL'lerde
 * çalışmadığı için fetch → blob → object URL kalıbı bilinçli olarak kullanılıyor.
 */
export const downloadLibraryItem = async (item: LibraryItem) => {
  const url = getItemUrl(item);
  if (!url) return;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = getItemFileName(item);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};
